import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import { Coffee } from "lucide-react";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import CustomerHome from "@/pages/customer/CustomerHome";
import StaffDashboard from "@/pages/staff/StaffDashboard";

function LoadingScreen() {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
      <div className="flex flex-col items-center gap-3 text-[#4A2C2A]">
        <Coffee className="w-8 h-8 animate-pulse" />
        <span className="text-sm font-medium">{t("loading")}</span>
      </div>
    </div>
  );
}

function Protected({ children, staffOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (location.state?.user) return children;
  if (loading) return <LoadingScreen />;
  if (!user) {
    const pendingTable = new URLSearchParams(location.search).get("table");
    if (pendingTable) sessionStorage.setItem("pending_table", pendingTable);
    return <Navigate to="/login" replace />;
  }
  if (staffOnly && user.role !== "staff") return <Navigate to="/menu" replace />;
  return children;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "staff" ? "/staff" : "/menu"} replace />;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/google" element={<AuthCallback />} />
      <Route path="/menu" element={<Protected><CustomerHome /></Protected>} />
      <Route path="/staff" element={<Protected staffOnly><StaffDashboard /></Protected>} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
